import { ReactNode, useEffect, useMemo, useRef } from "react";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { LoadingIndicator } from "components";
import { useLanguageSwitch } from "hooks/useLanguageSwitch";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { keycloakService } from "services/keycloakService";
import { NotFoundView } from "views/notFoundView/notFoundView";

interface ContactGuardProps {
  children: ReactNode;
  impersonate?: boolean;
}

/**
 * Before rendering its children, sets the selected contact
 * either based on the keycloak linked contact, or based on
 * a contactDbId route param. If using contactDbId
 * param, also updates linkedContact in keycloak state (impersonation mode).
 * @param children children elements to render.
 * @param impersonate set to true to use contactDbId, otherwise defaults to keycloak linked contact.
 * @returns children
 */
export const ContactGuard = ({ children, impersonate }: ContactGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routeContactId = params?.contactDbId;
  const { linkedContact } = useKeycloak();
  const { setSelectedContact, setSelectedContactId, selectedContact } =
    useGetContractIdData();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  ) as URLSearchParams & { contactId: string | null };

  const queryParamContactId = searchParams.get("contactId");

  // Get savedContactId from storage (only read once on mount)
  // This is needed because for IB users, the savedContactId might be a sibling contact
  // that wouldn't be found in linkedContact's representees
  const savedContactIdFromStorage = useMemo(() => {
    let saved = null;
    try {
      saved = window.sessionStorage.getItem("selectedContactIdBeforeReload");
    } catch {
      // sessionStorage might be blocked
    }
    if (!saved) {
      try {
        saved = window.localStorage.getItem("selectedContactIdBeforeReload");
      } catch {
        // localStorage might be blocked
      }
    }
    return saved;
  }, []);

  // Capture the contact ID for fetching ONCE on initial mount
  // This prevents loops when URL changes during render cycle
  const contactIdForFetchRef = useRef<string | number | null | undefined>(null);
  if (contactIdForFetchRef.current === null) {
    contactIdForFetchRef.current = impersonate
      ? routeContactId
      : (queryParamContactId || savedContactIdFromStorage || linkedContact);
  }
  const contactIdForFetch = contactIdForFetchRef.current;

  //this data is expected to be null if the
  //user does not have access rights to the contactId
  const {
    data: contactInfoData,
    error,
    loading,
  } = useGetContactInfo(false, contactIdForFetch);

  // Clean up the saved contact ID from storage and use the value we already read above
  // This runs once on mount to remove the stored value after it's been used
  const savedContactId = useMemo(() => {
    const saved = savedContactIdFromStorage;

    // Clean up storage
    if (saved) {
      try {
        window.sessionStorage.removeItem("selectedContactIdBeforeReload");
      } catch {
        // sessionStorage might be blocked
      }
      try {
        window.localStorage.removeItem("selectedContactIdBeforeReload");
      } catch {
        // localStorage might be blocked
      }
    }

    return saved;
  }, [savedContactIdFromStorage]);

  // Capture the initial contactId from URL on first render (before any effects modify it)
  const initialContactIdRef = useRef<string | null>(null);
  if (initialContactIdRef.current === null) {
    initialContactIdRef.current = queryParamContactId || savedContactId || "";
  }

  // Use saved contactId if query param is not present (after language reload)
  const effectiveContactId = queryParamContactId || savedContactId || (initialContactIdRef.current || null);

  // Track if initial contact selection has been processed
  const initialContactProcessedRef = useRef(false);

  useEffect(() => {
    if (!selectedContact?.id || !linkedContact) return;

    // Don't modify URL until initial contact from URL/localStorage has been processed
    if (!initialContactProcessedRef.current && initialContactIdRef.current) {
      return;
    }

    if (selectedContact?.id?.toString() !== linkedContact?.toString())
      searchParams.set("contactId", selectedContact?.id.toString());
    else searchParams.delete("contactId");

    navigate({ search: searchParams.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id, navigate]);

  //set the user to the app
  useEffect(() => {
    // check if the user has access to the contact data
    if (contactInfoData?.contactId) {
      // override user's linked contact with the route param if impersonating
      if (impersonate && routeContactId && routeContactId !== linkedContact) {
        keycloakService.setLinkedContact(routeContactId);
      }

      // Check if the fetched contact IS the requested contact (e.g., after language reload)
      // This handles the case where we fetched with savedContactId/queryParamContactId directly
      const fetchedContactMatchesRequested =
        effectiveContactId &&
        +contactInfoData.contactId === +effectiveContactId;

      // Try to find the requested contact in representees (for in-session navigation)
      const queryParamBasedContact =
        effectiveContactId &&
        !fetchedContactMatchesRequested &&
        contactInfoData.representees?.find(
          (representee) => +representee.id === +effectiveContactId
        );

      // Priority: 1) Fetched contact matches requested, 2) Found in representees, 3) Default to fetched contact
      if (fetchedContactMatchesRequested) {
        setSelectedContactId(contactInfoData?.contactId);
        setSelectedContact({
          id: contactInfoData?.contactId,
          contactId: contactInfoData?._contactId,
          userName: contactInfoData?.name,
        });
      } else if (queryParamBasedContact && queryParamBasedContact.contactId) {
        setSelectedContactId(queryParamBasedContact.id);
        setSelectedContact({
          id: queryParamBasedContact.id,
          contactId: queryParamBasedContact.contactId,
          userName: queryParamBasedContact.name,
        });
      } else if (contactInfoData?.contactId !== selectedContact?.contactId) {
        setSelectedContactId(contactInfoData?.contactId);
        setSelectedContact({
          id: contactInfoData?.contactId,
          contactId: contactInfoData?._contactId,
          userName: contactInfoData?.name,
        });
      }

      // Mark initial contact processing as complete to allow URL sync effect to run
      initialContactProcessedRef.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonate, routeContactId, linkedContact, contactInfoData?.contactId]);

  const { isReady: isLanguageReady } = useLanguageSwitch();

  if (error || (!loading && !contactInfoData?.contactId))
    return <NotFoundView />;

  if (loading || !isLanguageReady)
    return (
      <div className="flex justify-center items-center w-screen h-screen">
        <LoadingIndicator />
      </div>
    );

  return <>{children}</>;
};
